import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import moment from "moment";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CampaignLogTimeline } from "./CampaignLogTimeline";
import { toast } from "sonner";
import { selectCampaignDetails, selectCampaignLogGroupsByCampaignId } from "@/common/reducers/CampaignSelectors";
import { Fragment, useCallback, useState } from "react";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronRight, Copy } from "lucide-react";

interface CampaignGroupTableProps {
    campaignIds: string[];
}

export function CampaignGroupTable({ campaignIds }: CampaignGroupTableProps) {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

    const toggleRow = useCallback((id: string) => {
        setExpandedRows(prev => {
            const isExpanded = prev[id];
            return {
                ...prev,
                [id]: !isExpanded
            };
        });
    }, []);

    if (campaignIds.length === 0) {
        return (
            <div className="text-center py-8 text-slate-500">
                No campaign logs found
            </div>
        );
    }

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead>Campaign Name</TableHead>
                        <TableHead>Campaign ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Logs</TableHead>
                        <TableHead>Latest Activity</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {campaignIds.map((campaignId) => (
                        <CampaignGroupTableRow 
                            key={campaignId}
                            campaignId={campaignId}
                            isExpanded={expandedRows[campaignId] || false}
                            onToggle={() => toggleRow(campaignId)}
                        />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function CampaignGroupTableRow({ 
    campaignId, 
    isExpanded, 
    onToggle 
}: { 
    campaignId: string; 
    isExpanded: boolean; 
    onToggle: () => void;
}) {
    const campaignDetails = useAppSelector(state => selectCampaignDetails(state, campaignId));
    const logGroups = useAppSelector(state => selectCampaignLogGroupsByCampaignId(state, campaignId));
    
    const allLogs = logGroups.flat();
    const latestLog = allLogs.length > 0 ? allLogs[0] : null;
    
    // CampaignLog.campaign is the bt-common Campaign snapshot (#53);
    // consumer reads template.name / campaignStatus, both canonical.
    const logCampaign = latestLog?.campaign;

    // Determine Campaign Name (Template Name)
    const templateName = campaignDetails?.template?.name || logCampaign?.template?.name || 'no template';

    // Determine Status
    const status = logCampaign?.campaignStatus || campaignDetails?.campaignStatus || 'unknown';

    const handleCopyId = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(campaignId);
        toast.success('Campaign ID copied to clipboard');
    };

    const formatStatus = (s: string) => {
        return s.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getStatusColor = (s: string) => {
        if (s.includes('completed') || s.includes('delivered')) return 'bg-green-50 text-green-700 border-green-200';
        if (s.includes('paused')) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
        if (s.includes('aborted') || s.includes('error')) return 'bg-red-50 text-red-700 border-red-200';
        return 'bg-blue-50 text-blue-700 border-blue-200';
    };

    return (
        <Fragment>
            <TableRow className="cursor-pointer hover:bg-slate-50" onClick={onToggle}>
                <TableCell>
                    <Collapsible open={isExpanded} >
                        <CollapsibleTrigger asChild>
                            <div 
                                className="cursor-pointer p-1"
                            >
                                <ChevronRight
                                    className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                />
                            </div>
                        </CollapsibleTrigger>
                    </Collapsible>
                </TableCell>
                <TableCell>
                    <div className="font-medium">
                        {templateName}
                    </div>
                </TableCell>
                <TableCell>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500 truncate max-w-[100px]" title={campaignId}>
                            {campaignId}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={handleCopyId}
                        >
                            <Copy className="h-3 w-3" />
                        </Button>
                    </div>
                </TableCell>
                <TableCell>
                    <Badge variant="outline" className={getStatusColor(status)}>
                        {formatStatus(status)}
                    </Badge>
                </TableCell>
                <TableCell>
                    <Badge variant="secondary">
                        {allLogs.length} Logs
                    </Badge>
                </TableCell>
                <TableCell>
                    <span className="text-sm text-slate-600">
                        {latestLog ? moment(latestLog.createdAt).fromNow() : '-'}
                    </span>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow>
                    <TableCell colSpan={6} className="p-0 border-b">
                        <div className="p-4 bg-slate-50/50 space-y-4">
                            <div className="pl-9">
                                <CampaignLogTimeline 
                                    logs={allLogs} 
                                    campaignId={campaignId} 
                                />
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </Fragment>
    );
}
